import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Search,
    BookOpen,
    TrendingUp,
    FolderOpen,
    Video,
    Clock,
    ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SearchPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { t, language, getLocalizedField } = useLanguage();
    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('lessons');

    useEffect(() => {
        const q = searchParams.get('q');
        if (q) {
            setQuery(q);
            performSearch(q);
        }
    }, [searchParams]);

    const performSearch = async (searchQuery) => {
        if (!searchQuery.trim()) return;
        
        setLoading(true);
        try {
            const response = await axios.get(`${API}/search?q=${encodeURIComponent(searchQuery)}`);
            setResults(response.data);
        } catch (error) {
            console.error('Error searching:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (query.trim()) {
            searchParams.set('q', query);
            setSearchParams(searchParams);
        }
    };

    const getTotalResults = () => {
        if (!results) return 0;
        return (results.lessons?.length || 0) +
            (results.market_insights?.length || 0) +
            (results.resources?.length || 0) +
            (results.live_sessions?.length || 0);
    };

    return (
        <DashboardLayout>
            <div className="space-y-6 animate-fade-in">
                {/* Search Header */}
                <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-slate-900">
                        {t('searchResults')}
                    </h1>
                    <form onSubmit={handleSearch} className="mt-4 flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                type="search"
                                placeholder={t('searchPlaceholder')}
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                className="pl-10"
                                data-testid="search-input"
                            />
                        </div>
                        <Button type="submit" data-testid="search-btn">
                            <Search className="w-4 h-4 mr-2" />
                            {language === 'id' ? 'Cari' : 'Search'}
                        </Button>
                    </form>
                </div>

                {loading ? (
                    <div className="space-y-4">
                        {[1, 2, 3, 4].map((i) => (
                            <Skeleton key={i} className="h-24" />
                        ))}
                    </div>
                ) : results ? (
                    <>
                        <p className="text-slate-500">
                            {getTotalResults()} {language === 'id' ? 'hasil ditemukan untuk' : 'results found for'} "{searchParams.get('q')}"
                        </p>

                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="w-full md:w-auto grid grid-cols-4 md:flex">
                                <TabsTrigger value="lessons" data-testid="tab-lessons">
                                    {t('lessons')} ({results.lessons?.length || 0})
                                </TabsTrigger>
                                <TabsTrigger value="insights" data-testid="tab-insights">
                                    {t('marketInsight')} ({results.market_insights?.length || 0})
                                </TabsTrigger>
                                <TabsTrigger value="resources" data-testid="tab-resources">
                                    {t('resourceVault')} ({results.resources?.length || 0})
                                </TabsTrigger>
                                <TabsTrigger value="sessions" data-testid="tab-sessions">
                                    {t('liveSession')} ({results.live_sessions?.length || 0})
                                </TabsTrigger>
                            </TabsList>

                            {/* Lessons */}
                            <TabsContent value="lessons" className="mt-6 space-y-4">
                                {results.lessons?.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        {t('noResults')}
                                    </div>
                                ) : (
                                    results.lessons?.map((lesson) => (
                                        <Link key={lesson.id} to={`/lesson/${lesson.id}`}>
                                            <Card className="card-hover" data-testid={`result-lesson-${lesson.id}`}>
                                                <CardContent className="py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                                                            <BookOpen className="w-5 h-5 text-blue-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-medium text-slate-900 truncate">
                                                                {getLocalizedField(lesson, 'title')}
                                                            </h3>
                                                            <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                                                                <Clock className="w-3.5 h-3.5" />
                                                                {lesson.duration_minutes} {t('minutes')}
                                                            </p>
                                                        </div>
                                                        <ArrowRight className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </Link>
                                    ))
                                )}
                            </TabsContent>

                            {/* Market Insights */}
                            <TabsContent value="insights" className="mt-6 space-y-4">
                                {results.market_insights?.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        {t('noResults')}
                                    </div>
                                ) : (
                                    results.market_insights?.map((insight) => (
                                        <Link key={insight.id} to={`/insights?category=${insight.category}`}>
                                            <Card className="card-hover" data-testid={`result-insight-${insight.id}`}>
                                                <CardContent className="py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                                                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-medium text-slate-900 truncate">
                                                                {getLocalizedField(insight, 'title')}
                                                            </h3>
                                                            <p className="text-sm text-slate-500 line-clamp-1 mt-1">
                                                                {getLocalizedField(insight, 'content')}
                                                            </p>
                                                        </div>
                                                        <ArrowRight className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </Link>
                                    ))
                                )}
                            </TabsContent>

                            {/* Resources */}
                            <TabsContent value="resources" className="mt-6 space-y-4">
                                {results.resources?.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        {t('noResults')}
                                    </div>
                                ) : (
                                    results.resources?.map((resource) => (
                                        <Link key={resource.id} to={`/resources?category=${resource.category}`}>
                                            <Card className="card-hover" data-testid={`result-resource-${resource.id}`}>
                                                <CardContent className="py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                                                            <FolderOpen className="w-5 h-5 text-purple-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-medium text-slate-900 truncate">
                                                                {getLocalizedField(resource, 'title')}
                                                            </h3>
                                                            <p className="text-sm text-slate-500 line-clamp-1 mt-1">
                                                                {getLocalizedField(resource, 'description')}
                                                            </p>
                                                        </div>
                                                        <ArrowRight className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </Link>
                                    ))
                                )}
                            </TabsContent>

                            {/* Live Sessions */}
                            <TabsContent value="sessions" className="mt-6 space-y-4">
                                {results.live_sessions?.length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        {t('noResults')}
                                    </div>
                                ) : (
                                    results.live_sessions?.map((session) => (
                                        <Link key={session.id} to={`/sessions?category=${session.category}`}>
                                            <Card className="card-hover" data-testid={`result-session-${session.id}`}>
                                                <CardContent className="py-4">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                                                            <Video className="w-5 h-5 text-amber-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h3 className="font-medium text-slate-900 truncate">
                                                                {getLocalizedField(session, 'title')}
                                                            </h3>
                                                            <p className="text-sm text-slate-500 flex items-center gap-2 mt-1">
                                                                <Clock className="w-3.5 h-3.5" />
                                                                {session.duration_minutes} {t('minutes')}
                                                            </p>
                                                        </div>
                                                        <ArrowRight className="w-5 h-5 text-slate-400" />
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        </Link>
                                    ))
                                )}
                            </TabsContent>
                        </Tabs>
                    </>
                ) : (
                    <div className="text-center py-12">
                        <Search className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                        <p className="text-slate-500">
                            {language === 'id' 
                                ? 'Masukkan kata kunci untuk mencari'
                                : 'Enter a keyword to search'
                            }
                        </p>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};
